import { tabTypes } from "../constants/tab-types";
import {
  default as apiService,
  default as ApiService,
} from "../util/ApiService";
import {
  buildShareUrlForNonProxyUser,
  buildShareUrlForProxyUser,
  isProxyUser,
} from "../util/buildShareLink";
import { getDataFromDetailsSectionCache } from "../util/getDataFromDetailsSectionCache";
import { preprocessTopic } from "../util/preprocessTopic";
import { EVENT_BUS_EVENTS } from "../util/useEventBus";
import {
  ADD_CONTENT_NOTIFICATION,
  CLEAR_CONTENT_NOTIFICATION,
  CLEAR_FULL_TOPIC_ERROR,
  CLEAR_MERATIVE_LINK_WITH_TOKEN,
  COLLECT_ITEM_VIEWS_EVENT,
  COPY_TO_CLIPBOARD_ERROR,
  COPY_TO_CLIPBOARD_SUCCESS,
  DELETE_CONTENT_BOOKMARK,
  DELETE_CONTENT_BOOKMARK_ERROR,
  DELETE_CONTENT_BOOKMARK_SUCCESS,
  DELETE_CONTENT_FOLLOW,
  DELETE_CONTENT_FOLLOW_ERROR,
  DELETE_CONTENT_FOLLOW_SUCCESS,
  DRUG_MOBILE_TOC_SET_TAB,
  GET_ALL_SECTIONS_ERROR,
  GET_ALL_SECTIONS_SUCCESS,
  GET_CONTENT,
  GET_CONTENT_BAD_SECTION_REQUEST,
  GET_CONTENT_ERROR,
  GET_CONTENT_SUCCESS,
  GET_DETAILS_SECTION,
  GET_DETAILS_SECTION_ERROR,
  GET_DETAILS_SECTION_LOAD_EXISTING,
  GET_DETAILS_SECTION_SUCCESS,
  GET_FT_LINKS,
  GET_FT_LINKS_ERROR,
  GET_FT_LINKS_SUCCESS,
  GET_FULL_TOPIC,
  GET_FULL_TOPIC_ERROR,
  GET_FULL_TOPIC_SUCCESS,
  MARK_FULL_TOPIC_LOADED,
  GET_MERATIVE_LINK_WITH_TOKEN_ERROR,
  GET_MERATIVE_LINK_WITH_TOKEN_SUCCESS,
  GET_PRINT_DETAILS_SECTIONS,
  GET_PRINT_DETAILS_SECTIONS_ERROR,
  GET_PRINT_DETAILS_SECTIONS_SUCCESS,
  GET_PRINT_SECTIONS,
  GET_PRINT_SECTIONS_ERROR,
  GET_PRINT_SECTIONS_SUCCESS,
  GET_SECTION,
  GET_SECTION_ERROR,
  GET_SECTION_LOAD_EXISTING,
  GET_SECTION_SUCCESS,
  GET_SHARE_DATA,
  GET_SHARE_DATA_ERROR,
  GET_SHARE_DATA_SUCCESS,
  HIDE_ERROR,
  INITIALIZE_STORE,
  POST_ITEM_VIEWS,
  POST_ITEM_VIEWS_ERROR,
  POST_ITEM_VIEWS_SUCCESS,
  PREPROCESS_FULL_TOPIC,
  PREPROCESS_FULL_TOPIC_ERROR,
  PREPROCESS_FULL_TOPIC_SUCCESS,
  PUT_CONTENT_BOOKMARK,
  PUT_CONTENT_BOOKMARK_ERROR,
  PUT_CONTENT_BOOKMARK_SUCCESS,
  PUT_CONTENT_FOLLOW,
  PUT_CONTENT_FOLLOW_ERROR,
  PUT_CONTENT_FOLLOW_SUCCESS,
  SAVE_CLOUDINARY_TOKEN,
  SET_ACTIVE_TOC_ITEM,
  SET_ACTIVE_VIDEO,
  SHOW_ERROR,
} from "./constants";

const GENERIC_ERROR_MESSAGE =
  "We're sorry! An error occurred. Please try again.";

export const genericErrorViewModel = {
  name: "Share",
  message:
    "We're Sorry! Sharing is temporarily unavailable. We are working on resolving the problem. Please try again shortly",
};

const handleBadSectionRequest = async ({
  dispatch,
  slugPubType,
  slugTitle,
  tabType,
  loadFallback,
}) => {
  dispatch({
    type: GET_CONTENT_BAD_SECTION_REQUEST,
  });
  if (loadFallback) {
    await getContent(
      dispatch,
      slugPubType,
      slugTitle,
      tabType,
      undefined,
      undefined
    );
  }
};

export const getContent = async (
  dispatch,
  slugPubType,
  slugTitle,
  tabType,
  sectionIds,
  pathSectionId
) => {
  dispatch({ type: GET_CONTENT });
  try {
    const response = await apiService.getContent(
      slugPubType,
      slugTitle,
      tabType,
      tabType === tabTypes.DETAILS ? undefined : sectionIds,
      pathSectionId
    );
    if (
      response?.userExperience?.isPreviewMode &&
      !response.userExperience.isFreeArticle
    ) {
      dispatch({ type: GET_CONTENT_SUCCESS, payload: response });
    } else {
      if (!response?.contentSections?.length) {
        await handleBadSectionRequest({
          response,
          dispatch,
          slugPubType,
          slugTitle,
          tabType,
          loadFallback: sectionIds?.length > 0,
        });
      } else {
        dispatch({
          type: GET_CONTENT_SUCCESS,
          payload: { ...response, sectionIds },
        });
      }
    }
  } catch (error) {
    if (
      error.response?.status === 404 &&
      tabType === tabTypes.DETAILS &&
      pathSectionId
    ) {
      await handleBadSectionRequest({
        dispatch,
        slugPubType,
        slugTitle,
        tabType,
        loadFallback: !!pathSectionId,
      });
    } else {
      dispatch({ type: GET_CONTENT_ERROR, error: error.response?.data });
    }
  }
};

export const sectionSuccess = (payload) => ({
  type: GET_SECTION_SUCCESS,
  payload,
});

export const sectionLoadExisting = (payload) => ({
  type: GET_SECTION_LOAD_EXISTING,
  payload,
});

export const sectionError = (payload) => ({
  type: GET_SECTION_ERROR,
  payload,
});

export const detailsSectionSuccess = (payload) => ({
  type: GET_DETAILS_SECTION_SUCCESS,
  payload,
});

export const detailsSectionLoadExisting = (payload) => ({
  type: GET_DETAILS_SECTION_LOAD_EXISTING,
  payload,
});

export const detailSectionError = (payload) => ({
  type: GET_DETAILS_SECTION_ERROR,
  payload,
});

export const printSectionSuccess = (payload) => ({
  type: GET_PRINT_SECTIONS_SUCCESS,
  payload,
});

export const printSectionError = (payload) => ({
  type: GET_PRINT_SECTIONS_ERROR,
  payload,
});

export const printDetailsSectionSuccess = (payload) => ({
  type: GET_PRINT_DETAILS_SECTIONS_SUCCESS,
  payload,
});

export const printDetailsSectionError = (payload) => ({
  type: GET_PRINT_DETAILS_SECTIONS_ERROR,
  payload,
});

export const allSectionsSuccess = (payload) => ({
  type: GET_ALL_SECTIONS_SUCCESS,
  payload,
});

export const allSectionsError = (payload) => ({
  type: GET_ALL_SECTIONS_ERROR,
  payload,
});

export const sectionNotFoundError = {
  title: "Section not found...",
  message:
    "Please note that the specific section linked to no longer exists. We apologize for any inconvenience.",
};

export const sectionLoadingError = {
  title: "Section loading error...",
  message:
    "We're sorry! There was a problem loading this section. Please try again shortly.",
};

export const getSectionByHash = async (params) => {
  const {
    dispatch,
    slugPubType,
    slugTitle,
    sectionFromCache,
    idToSectionMap,
    toc,
    targetId,
    isNonFreePreview,
    drugMobileTocActiveTab,
  } = params;

  dispatch({ type: GET_SECTION });

  if (sectionFromCache) {
    return Promise.resolve(
      dispatch(
        sectionLoadExisting({
          section: sectionFromCache,
          localHash: targetId,
          drugMobileTocActiveTab,
        })
      )
    );
  }

  if (!isNonFreePreview) {
    const sectionIndex =
      (idToSectionMap[targetId] && idToSectionMap[targetId].index) ?? -1;
    const sectionId = toc[sectionIndex]?.reference;

    try {
      const payload = await apiService.getSection(
        slugPubType,
        slugTitle,
        sectionId
      );

      if (!payload?.message?.content?.length) {
        dispatch(
          sectionError({
            error: sectionNotFoundError.message,
          })
        );
      } else {
        dispatch(
          sectionSuccess({
            section: payload?.message?.content[0],
            localHash: targetId,
          })
        );
      }
    } catch (error) {
      dispatch(
        sectionError({
          error: sectionLoadingError.message,
        })
      );
    }
  }

  return undefined;
};

export const getDetailsSectionByHash = async (params) => {
  const {
    dispatch,
    slugPubType,
    slugTitle,
    detailsSectionCache,
    id,
    isNonFreePreview,
  } = params;

  dispatch({
    type: GET_DETAILS_SECTION,
  });

  const detailsSectionFoundInCache = getDataFromDetailsSectionCache(
    id,
    detailsSectionCache
  );

  if (detailsSectionFoundInCache) {
    return Promise.resolve(
      dispatch(
        detailsSectionLoadExisting({
          detailsSection: detailsSectionFoundInCache,
          localHash: id,
        })
      )
    );
  }

  if (!isNonFreePreview) {
    try {
      const payload = await apiService.getDetailsSection(
        slugPubType,
        slugTitle,
        id
      );

      if (!payload?.drugDetails?.sections?.length) {
        dispatch(
          detailSectionError({
            localHash: id,
            error: sectionNotFoundError.message,
          })
        );
      } else {
        dispatch(
          detailsSectionSuccess({
            detailsSection: payload?.drugDetails?.sections[0],
            localHash: id,
          })
        );
      }
    } catch (error) {
      dispatch(
        detailSectionError({
          sectionId: id,
          error: sectionLoadingError.message,
        })
      );
    }
  }

  return undefined;
};

export const getPrintSections = async (
  dispatch,
  slugPubType,
  slugTitle,
  sectionRefs
) => {
  dispatch({ type: GET_PRINT_SECTIONS });

  try {
    const payload = await apiService.getSection(
      slugPubType,
      slugTitle,
      sectionRefs
    );

    if (!payload?.message?.content?.length) {
      dispatch(
        printSectionError({
          error: sectionNotFoundError.message,
        })
      );
    } else {
      dispatch(
        printSectionSuccess({
          sections: payload?.message?.content,
        })
      );
    }
  } catch (error) {
    dispatch(
      printSectionError({
        error: sectionLoadingError.message,
      })
    );
  }
};

export const getPrintDetailsSections = async (
  dispatch,
  slugPubType,
  slugTitle,
  sectionIds
) => {
  dispatch({ type: GET_PRINT_DETAILS_SECTIONS });

  try {
    const payload = await apiService.getSelectedDetailsSections(
      slugPubType,
      slugTitle,
      sectionIds
    );

    if (payload?.length === 0) {
      dispatch(
        printDetailsSectionError({
          error: sectionNotFoundError.message,
        })
      );
    } else {
      dispatch(printDetailsSectionSuccess(payload));
    }
  } catch (error) {
    dispatch(
      printDetailsSectionError({
        error: sectionLoadingError.message,
      })
    );
  }

  return undefined;
};

export const getAllSections = async (dispatch, slugPubType, slugTitle) => {
  try {
    const payload = await apiService.getSection(slugPubType, slugTitle);

    if (!payload?.message?.content?.length) {
      dispatch(
        allSectionsError({
          error: sectionNotFoundError.message,
        })
      );
    } else {
      dispatch(
        allSectionsSuccess({
          sections: payload?.message?.content,
        })
      );
    }
  } catch (error) {
    dispatch(
      allSectionsError({
        error: sectionLoadingError.message,
      })
    );
  }
};

export async function fetchFullTextLinks(dispatch, docId, docType) {
  dispatch({ type: GET_FT_LINKS });
  try {
    const { data } = await apiService.get(
      `/api/content/ft-links?${docType}=${docId}`
    );
    dispatch({ type: GET_FT_LINKS_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: GET_FT_LINKS_ERROR, error: error.message });
  }
}

export const setActiveTocItem = (dispatch, reference) => {
  dispatch({ type: SET_ACTIVE_TOC_ITEM, payload: reference });
};

export const collectItemViewsEvent = (dispatch, event) => {
  dispatch({ type: COLLECT_ITEM_VIEWS_EVENT, payload: event });
};

export const postItemViews = async (
  dispatch,
  slugPubType,
  slugTitle,
  itemviews,
  eventBus
) => {
  dispatch({ type: POST_ITEM_VIEWS });
  try {
    const {
      data,
    } = await apiService.post(
      `/api/content/${slugPubType}/${slugTitle}/views`,
      { itemviews }
    );
    dispatch({ type: POST_ITEM_VIEWS_SUCCESS, payload: data });
    eventBus.emit(EVENT_BUS_EVENTS.ITEM_VIEWS_POSTED);
  } catch (error) {
    dispatch({ type: POST_ITEM_VIEWS_ERROR, error: error.message });
  }
};

export function setActiveVideo(dispatch, id) {
  dispatch({ type: SET_ACTIVE_VIDEO, payload: id });
}

export const showErrorMsg = (dispatch) => {
  dispatch({ type: SHOW_ERROR });
};

export const hideErrorMsg = (dispatch) => {
  dispatch({ type: HIDE_ERROR });
};

export const bookmarkContent = async (dispatch, slugPubType, slugTitle) => {
  try {
    dispatch({ type: PUT_CONTENT_BOOKMARK });
    await apiService.putBookmark(slugPubType, slugTitle);
    dispatch({ type: PUT_CONTENT_BOOKMARK_SUCCESS });
  } catch (error) {
    dispatch({ type: PUT_CONTENT_BOOKMARK_ERROR, error: error.response?.data });
  }
};

export const notifyCopyToClipboard = (dispatch, isSuccess, message) => {
  if (isSuccess) {
    dispatch({ type: COPY_TO_CLIPBOARD_SUCCESS, payload: message });
  } else {
    dispatch({ type: COPY_TO_CLIPBOARD_ERROR, payload: message });
  }
};

export const unbookmarkContent = async (dispatch, slugPubType, slugTitle) => {
  try {
    dispatch({ type: DELETE_CONTENT_BOOKMARK });
    await apiService.deleteBookmark(slugPubType, slugTitle);
    dispatch({ type: DELETE_CONTENT_BOOKMARK_SUCCESS });
  } catch (error) {
    dispatch({
      type: DELETE_CONTENT_BOOKMARK_ERROR,
      error: error.response?.data,
    });
  }
};

export const followContent = async (
  dispatch,
  slugPubType,
  slugTitle,
  subscriptionType,
  subscriptionMessage
) => {
  try {
    dispatch({ type: PUT_CONTENT_FOLLOW });
    await apiService.putContentSubscription(
      slugPubType,
      slugTitle,
      subscriptionType
    );
    dispatch({
      type: PUT_CONTENT_FOLLOW_SUCCESS,
      payload: subscriptionMessage,
    });
  } catch (error) {
    dispatch({
      type: PUT_CONTENT_FOLLOW_ERROR,
      error: error.response?.data,
    });
  }
};

export const unfollowContent = async (dispatch, slugPubType, slugTitle) => {
  try {
    dispatch({ type: DELETE_CONTENT_FOLLOW });
    await apiService.deleteContentSubscription(slugPubType, slugTitle);
    dispatch({ type: DELETE_CONTENT_FOLLOW_SUCCESS });
  } catch (error) {
    dispatch({
      type: DELETE_CONTENT_FOLLOW_ERROR,
      error: error.response?.data,
    });
  }
};

export function addErrorNotification(dispatch, errMsg) {
  dispatch({
    type: ADD_CONTENT_NOTIFICATION,
    payload: {
      error: errMsg,
    },
  });
}

export function clearNotification({ dispatch }) {
  dispatch({
    type: CLEAR_CONTENT_NOTIFICATION,
  });
}

export const updateIconAfterTopicUnfollow = ({ dispatch }) => {
  dispatch({
    type: DELETE_CONTENT_FOLLOW_SUCCESS,
  });
};

export const getFullTopic = async (
  dispatch,
  contentSectionCache,
  slugPubType,
  slugTitle,
  toc,
  tabType
) => {
  const allSectionsFromTopic = toc.map((section) => section.reference);
  const cachedSectionIds = contentSectionCache.reduce((acc, section) => {
    if (section?.id) {
      acc.push(section.id);
    }
    return acc;
  }, []);
  const sectionsToRetrieve = allSectionsFromTopic.filter(
    (sectionRef) => !cachedSectionIds.includes(sectionRef)
  );

  if (!sectionsToRetrieve.length) {
    dispatch({ type: MARK_FULL_TOPIC_LOADED });
    return;
  }

  dispatch({ type: GET_FULL_TOPIC });

  try {
    const payload = await apiService.getContent(
      slugPubType,
      slugTitle,
      tabType,
      sectionsToRetrieve
    );

    dispatch({ type: GET_FULL_TOPIC_SUCCESS, payload });
  } catch (error) {
    dispatch({ type: GET_FULL_TOPIC_ERROR, error: GENERIC_ERROR_MESSAGE });
  }
};

export const clearFullTopicLoadingError = (dispatch) =>
  dispatch({ type: CLEAR_FULL_TOPIC_ERROR });

export const preprocessFullTopic = (
  dispatch,
  content,
  state,
  preProcessorInstance
) => {
  try {
    dispatch({ type: PREPROCESS_FULL_TOPIC });

    const sections = preprocessTopic(content, state, preProcessorInstance);

    dispatch({ type: PREPROCESS_FULL_TOPIC_SUCCESS });

    return sections;
  } catch (e) {
    dispatch({
      type: PREPROCESS_FULL_TOPIC_ERROR,
      error: GENERIC_ERROR_MESSAGE,
    });
    return null;
  }
};

export const getCloudinaryToken = async (dispatch) => {
  const { cloudinaryToken } = await apiService.getCloudinaryToken();
  dispatch({ type: SAVE_CLOUDINARY_TOKEN, payload: cloudinaryToken });

  return cloudinaryToken;
};

export const setDrugMobileTocActiveTab = (dispatch, activeTab) => {
  dispatch({
    type: DRUG_MOBILE_TOC_SET_TAB,
    payload: activeTab,
  });
};

export const getMerativeLinkWithToken = async (dispatch, baseUrl) => {
  try {
    const payload = await apiService.getMerativeLinkWithToken(baseUrl);

    if (!payload?.merativeLink) {
      dispatch({
        type: GET_MERATIVE_LINK_WITH_TOKEN_ERROR,
        payload: { error: payload.errorMessage },
      });
    } else {
      dispatch({ type: GET_MERATIVE_LINK_WITH_TOKEN_SUCCESS, payload });
    }
  } catch (error) {
    dispatch({
      type: GET_MERATIVE_LINK_WITH_TOKEN_ERROR,
      payload: { error: GENERIC_ERROR_MESSAGE },
    });
  }
};

export const clearMerativeLinkWithToken = (dispatch) =>
  dispatch({ type: CLEAR_MERATIVE_LINK_WITH_TOKEN });

export const fetchShareLinkData = async (
  dispatch,
  { location, pubType, slug, productCode }
) => {
  dispatch({ type: GET_SHARE_DATA });
  const contentUrlData = {
    origin: location.origin,
    pubType,
    slug,
  };

  try {
    if (isProxyUser(location.hostname)) {
      dispatch({
        type: GET_SHARE_DATA_SUCCESS,
        payload: buildShareUrlForProxyUser(productCode, contentUrlData),
      });
      return;
    } else {
      const shareDetailsData = await ApiService.getShareDetailsData();

      dispatch({
        type: GET_SHARE_DATA_SUCCESS,
        payload: buildShareUrlForNonProxyUser(
          productCode,
          contentUrlData,
          shareDetailsData
        ),
      });
    }
  } catch (error) {
    dispatch({
      type: GET_SHARE_DATA_ERROR,
      error: genericErrorViewModel,
    });
  }
};

export const fetchPercRedirectLink = ApiService.fetchPercRedirectLink.bind(
  ApiService
);

export const initializeStore = (dispatch) => {
  dispatch({ type: INITIALIZE_STORE });
};
